import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../../utils/api';

const MaintenanceStandardPrint = ({ maintenance }) => {
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Recupero i dati aziendali dalla tabella CompanySettings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        setIsLoading(true);
        const companyId = localStorage.getItem('companyId');
        if (!companyId) {
          throw new Error('CompanyId not found in localStorage');
        }

        const response = await api.get('/company-settings', {
          headers: {
            'x-company-id': companyId
          }
        });
        
        // Mappa i dati dalle impostazioni al formato richiesto
        setCompanyData({
          name: response.data.companyName || 'FreeWheel',
          address: response.data.companyAddress || '',
          phone: response.data.companyPhone || '',
          city: response.data.companyCity || '',
          postalCode: response.data.companyPostalCode || '',
          province: response.data.companyProvince || '',
          email: response.data.companyEmail || ''
        });
      } catch (error) {
        console.error('Errore nel caricamento dei dati aziendali:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanySettings();
  }, []);

  // Creo l'URL con i parametri per il QR code
  const trackingUrl = new URL('https://freewheel-backend.onrender.com/track-maintenance');
  trackingUrl.searchParams.append('protocol', maintenance?.protocolNumber || '');
  trackingUrl.searchParams.append('phone', maintenance?.customerPhone || '');

  if (isLoading) {
    return <div>Caricamento dati aziendali...</div>;
  }

  return (
    <div id="standard-print" style={{
      width: '148mm',  // A5 width
      height: '210mm', // A5 height
      margin: '0 auto',
      padding: '10mm',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: '9pt',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      color: '#333',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header con info aziendali e logo */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '6mm',
        maxWidth: '100%'
      }}>
        <div style={{ maxWidth: '65%' }}>
          <div style={{ 
            fontSize: '14pt', 
            fontWeight: 'bold',
            color: '#DB1111',
            marginBottom: '2mm',
            wordBreak: 'break-word'
          }}>
            {companyData?.name}
          </div>
          <div style={{ 
            fontSize: '8pt', 
            color: '#666',
            lineHeight: '1.2'
          }}>
            <div style={{ wordBreak: 'break-word' }}>{companyData?.address}</div>
            <div>{companyData?.city} {companyData?.postalCode} {companyData?.province}</div>
            <div>Tel: {companyData?.phone}</div>
            <div>Email: {companyData?.email}</div>
          </div>
        </div>
        <div style={{
          backgroundColor: '#f8f8f8',
          padding: '2mm',
          borderRadius: '2px',
          border: '1px solid #eee',
          minWidth: '40mm',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '7pt', color: '#666' }}>
            Protocollo
          </div>
          <div style={{ fontSize: '12pt', fontWeight: 'bold', color: '#DB1111' }}>
            #{maintenance?.protocolNumber}
          </div>
        </div>
      </div>

      {/* Titolo Documento */}
      <div style={{
        textAlign: 'center',
        fontSize: '14pt',
        fontWeight: 'bold',
        margin: '6mm 0',
        color: '#333',
        borderBottom: '2px solid #DB1111',
        paddingBottom: '2mm'
      }}>
        SCHEDA DI MANUTENZIONE
      </div>

      {/* Info Cliente e Data */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '3mm',
        marginBottom: '6mm',
        backgroundColor: '#f8f8f8',
        padding: '3mm',
        borderRadius: '2px'
      }}>
        <div>
          <div style={{ fontSize: '8pt', color: '#666', marginBottom: '1mm' }}>Cliente</div>
          <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.customerName}</div>
        </div>
        <div>
          <div style={{ fontSize: '8pt', color: '#666', marginBottom: '1mm' }}>Telefono</div>
          <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.customerPhone}</div>
        </div>
        <div>
          <div style={{ fontSize: '8pt', color: '#666', marginBottom: '1mm' }}>Data</div>
          <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{format(new Date(), 'dd/MM/yyyy', { locale: it })}</div>
        </div>
      </div>

      {/* Dettagli Veicolo */}
      <div style={{
        marginBottom: '6mm',
        border: '1px solid #eee',
        borderRadius: '2px',
        padding: '3mm'
      }}>
        <div style={{ 
          fontWeight: 'bold', 
          marginBottom: '2mm',
          color: '#DB1111',
          fontSize: '10pt'
        }}>
          DETTAGLI VEICOLO
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3mm' }}>
          <div>
            <div style={{ fontSize: '8pt', color: '#666', marginBottom: '1mm' }}>Marca</div>
            <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.bikeBrand}</div>
          </div>
          <div>
            <div style={{ fontSize: '8pt', color: '#666', marginBottom: '1mm' }}>Modello</div>
            <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.bikeModel}</div>
          </div>
        </div>
      </div>

      {/* Descrizione Intervento */}
      <div style={{ marginBottom: '8mm' }}>
        <div style={{ 
          fontWeight: 'bold',
          marginBottom: '2mm',
          color: '#DB1111',
          fontSize: '10pt'
        }}>
          DESCRIZIONE INTERVENTO
        </div>
        <div style={{ 
          minHeight: '20mm',
          maxHeight: '40mm',
          padding: '3mm',
          border: '1px solid #eee',
          borderRadius: '2px',
          backgroundColor: '#f8f8f8',
          fontSize: '9pt',
          lineHeight: '1.3',
          overflow: 'hidden',
          wordBreak: 'break-word'
        }}>
          {maintenance?.description || ''}
        </div>
      </div>

      {/* Footer con QR Code e Importi */}
      <div style={{
        position: 'absolute',
        bottom: '10mm',
        left: '10mm',
        right: '10mm',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        borderTop: '1px solid #eee',
        paddingTop: '4mm',
        maxWidth: 'calc(100% - 20mm)'
      }}>
        {/* QR Code e istruzioni */}
        <div style={{ maxWidth: '45%' }}>
          <QRCodeSVG 
            value={trackingUrl.toString()}
            size={50}
            level="M"
            includeMargin={false}
          />
          <div style={{ 
            fontSize: '7pt', 
            color: '#666', 
            marginTop: '2mm',
            lineHeight: '1.2' 
          }}>
            Scansiona il QR Code per verificare lo stato della manutenzione
          </div>
        </div>

        {/* Importi */}
        <div style={{ textAlign: 'right', minWidth: '45mm' }}>
          <div style={{ marginBottom: '2mm' }}>
            <div style={{ fontSize: '8pt', color: '#666' }}>Acconto versato</div>
            <div style={{ fontSize: '11pt', fontWeight: 'bold', color: '#DB1111' }}>
              €{maintenance?.deposit ? parseFloat(maintenance.deposit).toFixed(2) : '0,00'}
            </div>
          </div>
          {maintenance?.estimatedCost && (
            <div>
              <div style={{ fontSize: '8pt', color: '#666' }}>Preventivo stimato</div>
              <div style={{ fontSize: '11pt', fontWeight: 'bold', color: '#DB1111' }}>
                €{parseFloat(maintenance.estimatedCost).toFixed(2)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stili di stampa */}
      <style>
        {`
          @media print {
            @page {
              size: 148mm 210mm;
              margin: 0;
              padding: 0;
            }
            body * {
              visibility: hidden;
            }
            #standard-print, #standard-print * {
              visibility: visible;
            }
            #standard-print {
              position: fixed !important;
              left: 0 !important;
              top: 0 !important;
              width: 148mm !important;
              height: 210mm !important;
              margin: 0 !important;
              padding: 10mm !important;
              overflow: hidden !important;
              page-break-after: always !important;
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default MaintenanceStandardPrint; 